import uniqBy from 'lodash/uniqBy';
import type { ApolloError } from 'apollo-client';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action, StoreActionApi } from '@atlassian/react-sweet-state';
import { fg } from '@atlassian/jira-feature-gating';
import { INITIAL_STATE } from '../constants.tsx';
import type { EpicCardType, BacklogEpicsStoreInitProps, State } from '../types.tsx';

export type InitContainerAction = () => Action<State, BacklogEpicsStoreInitProps, void>;

export const initContainer =
	() =>
	({ setState }: StoreActionApi<State>, initProps: BacklogEpicsStoreInitProps) => {
		setState({
			...initProps,
			isInitialised: true,
		});
	};

export const initState =
	({ cards, ...initProps }: BacklogEpicsStoreInitProps) =>
	({ setState, getState }: StoreActionApi<State>) => {
		const { cards: existingCards, cardsWithFullDetailsLoaded } = getState();

		const mergedCards = fg('prefetch-epics-data-backlog')
			? cards.map((card) => {
					if (cardsWithFullDetailsLoaded.has(card.epic.id)) {
						return existingCards.find(({ epic: { id } }) => id === card.epic.id) ?? card;
					}

					return card;
				})
			: cards;

		setState({
			...initProps,
			cards: mergedCards,
			isInitialised: true,
		});
	};

export const resetState =
	() =>
	({ setState }: StoreActionApi<State>) => {
		setState(INITIAL_STATE);
	};

export const updateQueryState =
	(newQueryState: { loading?: boolean; error?: ApolloError; refetchError?: ApolloError } = {}) =>
	({ setState }: StoreActionApi<State>) => {
		setState({
			...newQueryState,
		});
	};

export const addEpics =
	(epics: EpicCardType[], optimisticIds: number[] = []) =>
	({ setState, getState }: StoreActionApi<State>) => {
		// There is an implicit assumption that the optimisticIds match up 1-1 with the epics provided
		const { cards } = getState();

		if (!optimisticIds.length) {
			if (fg('prefetch-epics-data-backlog')) {
				setState({
					cards: [...cards, ...epics],
					cardsWithFullDetailsLoaded: new Set([
						...getState().cardsWithFullDetailsLoaded,
						...epics.map(({ epic: { id } }) => id),
					]),
				});
			} else {
				setState({
					cards: [...cards, ...epics],
				});
			}
			return;
		}

		const firstIndex = cards.findIndex(({ epic: { id } }) => optimisticIds[0] === id);
		const lastIndex = cards.findIndex(
			({ epic: { id } }) => optimisticIds[optimisticIds.length - 1] === id,
		);

		setState({
			cards: [...cards.slice(0, firstIndex), ...epics, ...cards.slice(lastIndex + 1)],
		});
	};

export const rankEpics =
	(epicIds: number[], newPosition: number) =>
	({ setState, getState }: StoreActionApi<State>) => {
		const { cards: existingCards } = getState();

		const cardsNotMoved = existingCards.filter(({ epic: { id } }) => !epicIds.includes(Number(id)));
		const cardsMoved = existingCards.filter(({ epic: { id } }) => epicIds.includes(Number(id)));

		setState({
			cards: [
				...cardsNotMoved.slice(0, newPosition),
				...cardsMoved,
				...cardsNotMoved.slice(newPosition),
			],
		});
	};

export const updateEpics =
	({
		updatedCards,
		startIndex = 0,
		hasNextPage,
	}: {
		updatedCards: EpicCardType[];
		startIndex?: number;
		hasNextPage: boolean;
	}) =>
	({ setState, getState }: StoreActionApi<State>) => {
		const { cards } = getState();

		cards.splice(
			startIndex,
			hasNextPage ? updatedCards.length : cards.length - startIndex,
			...updatedCards,
		);

		if (fg('prefetch-epics-data-backlog')) {
			setState({
				cards: uniqBy(cards, (card) => card.epic.id),
				cardsWithFullDetailsLoaded: new Set([
					...getState().cardsWithFullDetailsLoaded,
					...updatedCards.map(({ epic: { id } }) => id),
				]),
			});
		} else {
			setState({
				cards: uniqBy(cards, (card) => card.epic.id),
			});
		}
	};

export const updateEpicAttributes =
	(epicId: string, attributes: Partial<EpicCardType['epic']>) =>
	({ setState, getState }: StoreActionApi<State>) => {
		const { cards } = getState();
		const updatedCards = cards.map((card) => {
			if (card.epic.id === Number(epicId)) {
				return {
					...card,
					epic: {
						...card.epic,
						...attributes,
					},
				};
			}
			return card;
		});

		if (!fg('prefetch-epics-data-backlog')) {
			return setState({
				cards: updatedCards,
			});
		}

		const { cardsWithFullDetailsLoaded } = getState();

		const newCardsWithFullDetailsLoaded =
			attributes.isLoading !== undefined ? new Set(cardsWithFullDetailsLoaded) : null;

		if (newCardsWithFullDetailsLoaded) {
			if (attributes.isLoading) {
				newCardsWithFullDetailsLoaded.delete(Number(epicId));
			} else {
				newCardsWithFullDetailsLoaded.add(Number(epicId));
			}
		}

		setState({
			cards: updatedCards,
			cardsWithFullDetailsLoaded: newCardsWithFullDetailsLoaded ?? cardsWithFullDetailsLoaded,
		});
	};

export const updateEpicsAttributes =
	(attributes: Partial<EpicCardType['epic']>, startIndex = 0) =>
	({ setState, getState }: StoreActionApi<State>) => {
		const { cards: existingCards } = getState();

		const cards = existingCards.map((card, index) => ({
			...card,
			epic: {
				...card.epic,
				...(index >= startIndex && attributes),
			},
		}));

		if (!fg('prefetch-epics-data-backlog')) {
			return setState({ cards });
		}

		const { cardsWithFullDetailsLoaded } = getState();

		const newCardsWithFullDetailsLoaded =
			attributes.isLoading !== undefined ? new Set(cardsWithFullDetailsLoaded) : null;

		if (newCardsWithFullDetailsLoaded) {
			for (let i = startIndex; i < existingCards.length; i++) {
				if (attributes.isLoading) {
					newCardsWithFullDetailsLoaded.delete(existingCards[i].epic.id);
				} else {
					newCardsWithFullDetailsLoaded.add(existingCards[i].epic.id);
				}
			}
		}

		setState({
			cards,
			cardsWithFullDetailsLoaded: newCardsWithFullDetailsLoaded ?? cardsWithFullDetailsLoaded,
		});
	};

export const updateHasFullFetchStarted =
	(started: boolean) =>
	({ setState, getState }: StoreActionApi<State>) => {
		setState({
			hasFullFetchStarted: started || getState().hasFullFetchStarted,
		});
	};

export type Actions = {
	initContainer: InitContainerAction;
	initState: typeof initState;
	resetState: typeof resetState;
	updateQueryState: typeof updateQueryState;
	addEpics: typeof addEpics;
	rankEpics: typeof rankEpics;
	updateEpics: typeof updateEpics;
	updateEpicAttributes: typeof updateEpicAttributes;
	updateEpicsAttributes: typeof updateEpicsAttributes;
	updateHasFullFetchStarted: typeof updateHasFullFetchStarted;
};

export const actions: Actions = {
	initContainer,
	initState,
	resetState,
	updateQueryState,
	addEpics,
	rankEpics,
	updateEpics,
	updateEpicAttributes,
	updateEpicsAttributes,
	updateHasFullFetchStarted,
};
